import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/shared/components/ui/accordion';
import RichFilterCheckbox from 'src/shared/components/rich-filter-checkbox/RichFilterCheckbox';
import type { RiskScoreColour } from 'src/shared/components/risk-score-cell/riskScoreColour';

export type RiskLevel = { value: string; label: string; colour: RiskScoreColour };

export const RISK_LEVELS: RiskLevel[] = [
    { value: 'VERY_HIGH', label: 'Very High', colour: 'divergent-red-amber-green-1' },
    { value: 'HIGH', label: 'High', colour: 'divergent-red-amber-green-2' },
    { value: 'MEDIUM', label: 'Medium', colour: 'divergent-red-amber-green-3' },
    { value: 'LOW', label: 'Low', colour: 'divergent-red-amber-green-4' },
    { value: 'VERY_LOW', label: 'Very Low', colour: 'divergent-red-amber-green-5' },
];

// State of Nature: higher score = better condition, so colour order is inverted.
export const SON_LEVELS: RiskLevel[] = [
    { value: 'VERY_POOR', label: 'Very Poor', colour: 'divergent-red-amber-green-1' },
    { value: 'POOR', label: 'Poor', colour: 'divergent-red-amber-green-2' },
    { value: 'MODERATE', label: 'Moderate', colour: 'divergent-red-amber-green-3' },
    { value: 'GOOD', label: 'Good', colour: 'divergent-red-amber-green-4' },
    { value: 'VERY_GOOD', label: 'Very Good', colour: 'divergent-red-amber-green-5' },
];

type Props = {
    label: string;
    selectedValues: string[];
    onChange: (values: string[]) => void;
    levels?: RiskLevel[];
};

const RiskDimensionAccordion = ({ label, selectedValues, onChange, levels = RISK_LEVELS }: Props) => {
    const handleChange = (value: string, checked: boolean) => {
        if (checked) {
            onChange([...selectedValues, value]);
        } else {
            onChange(selectedValues.filter(v => v !== value));
        }
    };

    return (
        <Accordion type='single' collapsible>
            <AccordionItem value={label}>
                <AccordionTrigger data-testid={selectedValues.length > 0 ? 'risk-filter-count' : undefined}>
                    {label}
                    {selectedValues.length > 0 && ` (${selectedValues.length})`}
                </AccordionTrigger>
                <AccordionContent>
                    <fieldset className='flex flex-col gap-2 border-none p-0'>
                        <legend className='sr-only'>{label}</legend>
                        {levels.map(level => (
                            <RichFilterCheckbox
                                key={level.value}
                                label={level.label}
                                checked={selectedValues.includes(level.value)}
                                onChange={checked => handleChange(level.value, checked)}
                                colour={level.colour}
                            />
                        ))}
                    </fieldset>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
};

export default RiskDimensionAccordion;
